import React, { useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  Description,
  InsertLink,
  Phone,
  PlayCircleOutline,
  TouchApp
} from "@material-ui/icons";
import { buildOfficialTemplatePreviewModel } from "./officialTemplatePreviewModel";

const bubbleStyle = {
  background: "white",
  borderRadius: "4px 12px 12px",
  padding: "10px 12px",
  boxShadow: "0 1px 2px rgba(0,0,0,.12)",
  marginBottom: 7,
  overflow: "hidden"
};

const textStyle = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" };

const buttonIcon = type => {
  if (String(type).includes("URL")) return <InsertLink style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }} />;
  if (String(type).includes("PHONE")) return <Phone style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }} />;
  return <TouchApp style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }} />;
};

const MediaHeader = ({ media }) => {
  if (!media) return null;
  if (media.required) {
    return <Box data-testid="template-media-required" mb={1} p={1.2} bgcolor="#f2f2f2" borderRadius={8} color="#6b7280">
      <Typography variant="caption">Mídia do cabeçalho — obrigatória</Typography>
    </Box>;
  }
  if (media.format === "IMAGE") {
    return <Box
      data-testid="template-header-image"
      component="img"
      src={media.url}
      alt="Imagem do cabeçalho do template"
      mb={1}
      width="100%"
      maxHeight={210}
      style={{ display: "block", objectFit: "cover", borderRadius: 8 }}
    />;
  }
  const Icon = media.format === "VIDEO" ? PlayCircleOutline : Description;
  return <Box data-testid={`template-header-${media.format.toLowerCase()}`} mb={1} p={1.2} bgcolor="#f2f2f2" borderRadius={8} display="flex" alignItems="center" style={{ gap: 8 }}>
    <Icon color="action" />
    <Box minWidth={0}>
      <Typography variant="caption" style={{ fontWeight: 700 }}>{media.format === "VIDEO" ? "Vídeo do cabeçalho" : "Documento do cabeçalho"}</Typography>
      <Typography variant="caption" color="textSecondary" display="block" noWrap>{media.url}</Typography>
    </Box>
  </Box>;
};

const PreviewButtons = ({ buttons }) => {
  if (!buttons.length) return null;
  return <Box data-preview-section="BUTTONS">
    {buttons.map((button, index) => {
      const destination = button.url || button.phoneNumber;
      return <Box key={`${button.key}_${index}`} mt={1} pt={1} borderTop="1px solid #e6e6e6" textAlign="center" color="#1677d2" style={{ overflowWrap: "anywhere" }}>
        <Box>{buttonIcon(button.type)}{button.text}</Box>
        {destination && <Typography variant="caption" color="textSecondary" style={{ overflowWrap: "anywhere" }}>{destination}</Typography>}
      </Box>;
    })}
  </Box>;
};

const MissingVariables = ({ variables }) => {
  if (!variables.length) return null;
  return <Box
    data-testid="template-missing-variables"
    mt={0.5}
    px={1}
    py={0.5}
    bgcolor="#fff4db"
    border="1px solid #ffd27a"
    borderRadius={6}
    color="#8a5b00"
  >
    <Typography variant="caption">Preencher {variables.map(variable => `{{${variable}}}`).join(", ")}</Typography>
  </Box>;
};

const LegacyFallback = ({ template }) => {
  const buttons = Array.isArray(template?.responseButtons) ? template.responseButtons : [];
  return <Box style={bubbleStyle} data-testid="template-preview-fallback">
    <Typography variant="body2" style={textStyle}>{template?.message || template?.shortcode || "Selecione um template aprovado"}</Typography>
    <PreviewButtons buttons={buttons.map((button, index) => ({
      key: button.key || `button_${index + 1}`,
      text: button.label || button.text || "",
      type: button.type || "QUICK_REPLY"
    })).filter(button => button.text)} />
  </Box>;
};

function OfficialTemplatePreviewContent({ template, parameterConfig }) {
  const components = useMemo(
    () => buildOfficialTemplatePreviewModel(template, parameterConfig),
    [template, parameterConfig]
  );
  const hasRenderableComponent = components.some(component =>
    Boolean(component.media || component.rendered.text || component.buttons.length)
  );

  if (!components.length || !hasRenderableComponent) return <LegacyFallback template={template} />;

  return <Box style={bubbleStyle} data-testid="template-preview-v2">
    {components.map((component, index) => {
      const key = `${component.type}_${component.originalIndex}_${index}`;
      if (component.type === "HEADER") {
        return <Box key={key} data-preview-section="HEADER" mb={component.rendered.text ? 1 : 0}>
          <MediaHeader media={component.media} />
          {component.rendered.text && <Typography variant="subtitle2" style={{ ...textStyle, fontWeight: 700 }}>{component.rendered.text}</Typography>}
          <MissingVariables variables={component.rendered.missingVariables} />
        </Box>;
      }
      if (component.type === "BODY") {
        return component.rendered.text ? <Box key={key} data-preview-section="BODY">
          <Typography variant="body2" style={textStyle}>{component.rendered.text}</Typography>
          <MissingVariables variables={component.rendered.missingVariables} />
        </Box> : null;
      }
      if (component.type === "FOOTER") {
        return component.rendered.text ? <Typography key={key} data-preview-section="FOOTER" variant="caption" color="textSecondary" display="block" style={{ ...textStyle, marginTop: 8 }}>{component.rendered.text}</Typography> : null;
      }
      if (component.type === "BUTTONS") {
        return <PreviewButtons key={key} buttons={component.buttons} />;
      }
      return null;
    })}
  </Box>;
}

class TemplatePreviewBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return <LegacyFallback template={this.props.template} />;
    return this.props.children;
  }
}

export default function OfficialTemplatePreview({ template, parameterConfig }) {
  return <TemplatePreviewBoundary template={template}>
    <OfficialTemplatePreviewContent template={template} parameterConfig={parameterConfig} />
  </TemplatePreviewBoundary>;
}
